import { useMemo } from 'react';

import type { ICategoryServiceDto, IPolicyEligibleServicesDto } from '@/dtos/models';
import { ICategoryDtoCategoryTypeEnum } from '@/dtos/models/ICategoryDto';

const MAX_VISIBLE_SERVICES = 3;

const CATEGORY_NAME_TO_TYPE: Record<string, ICategoryDtoCategoryTypeEnum> = {
  medical: ICategoryDtoCategoryTypeEnum.MEDICAL,
  dental: ICategoryDtoCategoryTypeEnum.DENTAL,
  vision: ICategoryDtoCategoryTypeEnum.VISION,
  pharmacy: ICategoryDtoCategoryTypeEnum.PHARMACY,
  adoption: ICategoryDtoCategoryTypeEnum.ADOPTION,
  parking: ICategoryDtoCategoryTypeEnum.PARKING,
  transit: ICategoryDtoCategoryTypeEnum.TRANSIT,
  'dependent care': ICategoryDtoCategoryTypeEnum.DEPENDENT_CARE,
  'life style': ICategoryDtoCategoryTypeEnum.LIFE_STYLE,
  lifestyle: ICategoryDtoCategoryTypeEnum.LIFE_STYLE,
  wellness: ICategoryDtoCategoryTypeEnum.WELLNESS,
  travel: ICategoryDtoCategoryTypeEnum.TRAVEL,
  'plan specific': ICategoryDtoCategoryTypeEnum.PLAN_SPECIFIC,
  premium: ICategoryDtoCategoryTypeEnum.PREMIUM,
};

const resolveCategoryType = (category?: string): ICategoryDtoCategoryTypeEnum | undefined => {
  if (!category) return undefined;

  return CATEGORY_NAME_TO_TYPE[category.toLowerCase()];
};

export interface EligibleServiceCategory {
  id: number;
  category: string;
  categoryType?: ICategoryDtoCategoryTypeEnum;
  services: string[];
}

export interface FormattedEligibleServices {
  serviceNames: string[];
  categories: EligibleServiceCategory[];
  total: number;
  extraCount: number;
}

const formatCategory = (category: ICategoryServiceDto): EligibleServiceCategory => ({
  id: category.id || 0,
  category: category.category || '',
  categoryType: resolveCategoryType(category.category),
  services: (category.services || []).map((s) => s.name || '').filter(Boolean),
});

const useEligibleServicesFormatter = (data: IPolicyEligibleServicesDto[]): FormattedEligibleServices => {
  const formatted = useMemo(() => {
    const allNames: string[] = data.flatMap(
      (policy) =>
        (policy.categories || []).flatMap(
          (category) =>
            (category.services || []).map((service) => service.name || '').filter(Boolean),
        ),
    );

    const categories: EligibleServiceCategory[] = data.flatMap(
      (policy) => (policy.categories || []).map(formatCategory),
    );

    const total = data[0]?.total || allNames.length;
    const extraCount = Math.max(total - MAX_VISIBLE_SERVICES, 0);

    return { serviceNames: allNames, categories, total, extraCount };
  }, [data]);

  return formatted;
};

export default useEligibleServicesFormatter;
